"use client"

import Link from "next/link"
import { useState } from "react"
import { isAxiosError } from "axios"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"
import { ArrowLeftIcon, MailCheckIcon, SendIcon } from "lucide-react"

import { cn } from "@/utils/utils"
import { Card, CardContent } from "@/components/ui/card"
import {
  Field,
  FieldDescription,
  FieldGroup,
  FieldLabel,
} from "@/components/ui/field"
import { Input } from "@/components/ui/input"
import { LoadingButton } from "@/components/common/loading-button"
import { useForgotPasswordMutation } from "@/hooks/useAuthQuery"

const forgotPasswordSchema = z.object({
  email: z.string().email("E-mail inválido"),
})

type ForgotPasswordFormData = z.infer<typeof forgotPasswordSchema>

export function ForgotPasswordForm({
  className,
  ...props
}: React.ComponentProps<"div">) {
  const { mutate, isPending, error } = useForgotPasswordMutation()
  const [successMessage, setSuccessMessage] = useState<string | null>(null)

  const form = useForm<ForgotPasswordFormData>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: { email: "" },
  })

  function onSubmit(data: ForgotPasswordFormData) {
    mutate(data, {
      onSuccess: (response) => {
        setSuccessMessage(response.message)
      },
    })
  }

  const apiErrorMessage = isAxiosError(error)
    ? error.response?.data?.message
    : undefined

  return (
    <div className={cn("flex flex-col gap-6", className)} {...props}>
      <Card className="overflow-hidden p-0">
        <CardContent className="p-6 md:p-8">
          {successMessage ? (
            <FieldGroup>
              <div className="flex flex-col items-center gap-3 text-center">
                <div className="flex size-12 items-center justify-center rounded-full bg-primary/10 text-primary">
                  <MailCheckIcon className="size-6" />
                </div>
                <h1 className="text-2xl font-bold">Verifique seu e-mail</h1>
                <p className="text-balance text-muted-foreground">
                  {successMessage}
                </p>
              </div>
              <Field>
                <Link
                  href="/login"
                  className="inline-flex items-center justify-center gap-2 text-sm underline-offset-2 hover:underline"
                >
                  <ArrowLeftIcon className="size-4" /> Voltar para o login
                </Link>
              </Field>
            </FieldGroup>
          ) : (
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <FieldGroup>
                <div className="flex flex-col items-center gap-2 text-center">
                  <h1 className="text-2xl font-bold">Recuperar senha</h1>
                  <p className="text-balance text-muted-foreground">
                    Informe o e-mail da sua conta e enviaremos um link para você
                    redefinir a senha.
                  </p>
                </div>
                <Field>
                  <FieldLabel htmlFor="email">E-mail</FieldLabel>
                  <Input
                    id="email"
                    type="email"
                    placeholder="nome@exemplo.com"
                    autoComplete="email"
                    {...form.register("email")}
                  />
                  {form.formState.errors.email && (
                    <span className="text-sm text-destructive">
                      {form.formState.errors.email.message}
                    </span>
                  )}
                  {apiErrorMessage && (
                    <span className="text-sm text-destructive">
                      {apiErrorMessage}
                    </span>
                  )}
                </Field>
                <Field>
                  <LoadingButton type="submit" loading={isPending}>
                    <SendIcon data-icon="inline-start" /> Enviar link de recuperação
                  </LoadingButton>
                </Field>
                <FieldDescription className="text-center">
                  Lembrou a senha? <Link href="/login">Entrar</Link>
                </FieldDescription>
              </FieldGroup>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
